import React, { useState, useEffect } from 'react';
import { AlertTriangle, Info, X, AlertCircle } from 'lucide-react';

export interface UsageWarningBannerProps {
  message: string;
  mobileMessage?: string;
  actionLabel?: string;
  mobileActionLabel?: string;
  onAction?: () => void;
  dismissible?: boolean;
  variant?: 'info' | 'warning' | 'error';
  storageKey?: string;
}

export const UsageWarningBanner: React.FC<UsageWarningBannerProps> = ({
  message,
  mobileMessage,
  actionLabel,
  mobileActionLabel,
  onAction,
  dismissible = true,
  variant = 'info',
  storageKey,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (storageKey) {
      const dismissed = sessionStorage.getItem(storageKey);
      if (dismissed === 'true') {
        setIsVisible(false);
      }
    }
  }, [storageKey]);

  const handleDismiss = () => {
    setIsVisible(false);
    if (storageKey) {
      sessionStorage.setItem(storageKey, 'true');
    }
  };

  if (!isVisible) return null;

  const variantStyles = {
    error: {
      border: 'border-incorrect/30',
      bg: 'bg-incorrect/25',
      text: 'text-incorrect',
      hoverBg: 'hover:bg-incorrect/25',
      hoverText: 'hover:text-incorrect/80',
    },
    warning: {
      border: 'border-warning/30',
      bg: 'bg-warning/15',
      text: 'text-warning',
      hoverBg: 'hover:bg-warning/25',
      hoverText: 'hover:text-warning/80',
    },
    info: {
      border: 'border-info/30',
      bg: 'bg-info/15',
      text: 'text-info',
      hoverBg: 'hover:bg-info/25',
      hoverText: 'hover:text-info/80',
    },
  };

  const icons = {
    error: AlertCircle,
    warning: AlertTriangle,
    info: Info,
  };

  const Icon = icons[variant];
  const styles = variantStyles[variant];

  return (
    <div
      role="alert"
      className={`w-full border p-4 fixed bottom-0 left-0 right-0 z-50 flex rounded-none ${styles.border} ${styles.bg} px-4 py-1.5 ${styles.text}`}
    >
      <div className="mx-auto flex w-full max-w-7xl items-center justify-center gap-2 text-sm">
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />

          <span className="font-medium">
            {mobileMessage ? (
              <>
                <span className="sm:hidden">{mobileMessage}</span>
                <span className="hidden sm:inline">{message}</span>
              </>
            ) : (
              message
            )}
          </span>

          {actionLabel && onAction && (
            <button
              onClick={onAction}
              className={`inline-flex items-center justify-center whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 underline-offset-4 hover:underline rounded-lg h-auto p-0 text-sm font-semibold ${styles.text} underline ${styles.hoverText}`}
            >
              {mobileActionLabel ? (
                <>
                  <span className="sm:hidden">{mobileActionLabel}</span>
                  <span className="hidden sm:inline">{actionLabel}</span>
                </>
              ) : (
                actionLabel
              )}
            </button>
          )}
        </div>
      </div>

      {dismissible && (
        <button
          onClick={handleDismiss}
          className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:text-accent-foreground fixed bottom-1 right-1 z-50 h-6 w-6 p-0 ${styles.hoverBg}`}
          aria-label="Dismiss notification"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};
